import AlertMessage from "@/components/library/Alerts/AlertMessage";
import CodeSegments from "@/components/screens/Login/Components/CodeSegments";
import type { ILoginFormViewModel } from "@/components/screens/Login/Types/LoginTypes";
import { cn } from "@/utils/css";
import Button from "@dinesapp/dines-ui/components/Buttons/Button";
import CustomForm from "@dinesapp/dines-ui/components/Form/CustomForm";

/**
 * Interface representing the props for the shared login form component.
 * @interface ILoginFormProps
 * @property {ILoginFormViewModel} vm: Form view model produced by the login VM.
 */
interface ILoginFormProps {
  vm: ILoginFormViewModel;
}

const fieldErrorOverrides = cn(
  "[&_p.text-red-500]:mt-2",
  '[&_[data-testid$="-input-container"]:has(p.text-red-500)_input]:ring-2',
  '[&_[data-testid$="-input-container"]:has(p.text-red-500)_input]:ring-red-500',
);

const LoginForm = ({ vm }: ILoginFormProps) => {
  const primaryAction = vm.actions.find((action) => action.isSubmitButton);
  const secondaryActions = vm.actions.filter(
    (action) => !action.isSubmitButton,
  );

  return (
    <div className={cn("text-dines-purple-darkest", fieldErrorOverrides)}>
      {vm.errorMessage && (
        <div role="alert">
          <AlertMessage
            id="login-error-message"
            title={vm.errorMessage}
            className={cn(
              "mb-4 rounded-2xl border border-red-400/30 bg-red-500/[0.08] py-3",
              "[&_p]:text-red-100 [&_p]:text-sm [&_p]:leading-relaxed",
            )}
          />
        </div>
      )}

      {vm.codeSegments ? (
        <CodeSegments {...vm.codeSegments} />
      ) : (
        <CustomForm form={vm.form} layout={vm.layout} id={vm.labels.id} />
      )}

      {vm.forgotPasswordTrigger.isVisible && (
        <div className="mt-2 flex justify-end">
          <Button
            buttonType="bg-transparent text-white/70 hover:text-white"
            text={vm.forgotPasswordTrigger.text}
            id="forgot-password-trigger"
            width="w-auto"
            margin="m-0"
            padding="p-0"
            fontSize="text-sm"
            fontWeight="font-normal"
            border="border-white/20 border-b hover:border-dines-green"
            rounding="rounded-sm"
            outline="focus:outline-none focus-visible:ring-2 focus-visible:ring-dines-green"
            extraClasses="pb-px"
            onClick={vm.forgotPasswordTrigger.onClick}
          />
        </div>
      )}

      {vm.scanAction?.isVisible && (
        <div className="mt-6">
          <Button
            buttonType={vm.scanAction.buttonType}
            id={vm.scanAction.id}
            text={vm.scanAction.text}
            width="w-full"
            margin="m-0"
            padding="px-6 py-4"
            fontSize="text-base"
            onClick={vm.scanAction.onClick}
          />
        </div>
      )}

      {primaryAction && (
        <div className="mt-6">
          <Button
            buttonType={primaryAction.buttonType}
            isSubmitButton={primaryAction.isSubmitButton}
            form={vm.labels.id}
            id={primaryAction.id}
            text={
              primaryAction.isSubmitting
                ? primaryAction.loadingText
                : primaryAction.text
            }
            width="w-full"
            margin="m-0"
            padding="px-6 py-4"
            fontSize="text-base"
            disabled={primaryAction.isSubmitting}
            extraClasses={cn(
              primaryAction.isSubmitting && "cursor-not-allowed opacity-60",
            )}
            onClick={primaryAction.onClick}
          />
        </div>
      )}

      {secondaryActions.map((action) => (
        <div key={action.id} className="mt-5 text-center">
          <Button
            buttonType={action.buttonType}
            text={action.text}
            id={action.id}
            width="w-auto"
            margin="m-0"
            padding="p-0"
            fontSize="text-sm"
            fontWeight="font-normal"
            border="border-0"
            rounding="rounded-sm"
            outline="focus:outline-none focus-visible:ring-2 focus-visible:ring-dines-green"
            disabled={action.isSubmitting}
            onClick={action.onClick}
          />
        </div>
      ))}
    </div>
  );
};

export default LoginForm;
